<template>
  <div class="habilitation-container">
    <div class="dashboard">
      <img src="@/assets/logogth.png" alt="Logo GTH">
    </div>

    <div :style="backgroundStyle" class="banner">
      <h1>Habilitation électrique - NF C 18-510</h1>
    </div>

    <div class="grid-3col">
      <!-- Colonne Chapitres -->
      <div class="column">
        <h2>Chapitres</h2>
        <ul>
          <li v-for="(chapitre, i) in chapitres" :key="i" class="chapitre-li">
            <div class="chapitre-header">
              <button 
                :class="{ active: chapitreActif === i }" 
                @click="selectChapitre(i)"
                :aria-label="`Chapitre ${chapitre.nom}`"
              >
                {{ chapitre.nom }}
              </button>
              <button 
                class="play-chapitre" 
                @click="playChapitre(i)"
                aria-label="Lire la première vidéo du chapitre"
                title="Lire la première vidéo du chapitre"
              >
                ▶️ <span class="play-all-text">Striming</span>
              </button>
            </div>
          </li>
        </ul>
      </div>

      <!-- Colonne Modules -->
      <div class="column">
        <h2>Modules</h2>
        <ul v-if="chapitreActif !== null">
          <li v-for="(module, j) in chapitres[chapitreActif].modules" :key="j">
            <button 
              :class="{ active: moduleActif === j }" 
              @click="selectModule(j)"
              :aria-label="`Module ${module.nom}`"
            >
              {{ module.nom }}
            </button>
          </li>
        </ul>
      </div>

      <!-- Colonne Vidéos -->
      <div class="column">
        <h2>Vidéos</h2>
        <ul v-if="chapitreActif !== null && moduleActif !== null">
          <li v-for="(vid, k) in chapitres[chapitreActif].modules[moduleActif].videos" :key="k">
            <a 
              href="#" 
              @click.prevent="playVideo(vid)"
              :aria-label="`Lire ${vid.titre}`"
              title="Lire cette vidéo"
              class="video-link"
            >
              ▶️ {{ vid.titre }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- Modale Vidéo -->
    <div v-if="videoActive" class="modal-overlay" @click.self="fullscreenLock ? null : closeVideo()">
      <div class="modal modal-large" ref="videoContainer"
           @mousemove="handleMouseMove"
           @mouseleave="hideControlsNow"
           @contextmenu.prevent="hideControlsNow">
        <div class="modal-header">
          <h4>{{ videoActive.titre }}</h4>
          <button 
            @click="closeVideo" 
            class="close-btn"
            aria-label="Fermer la vidéo"
          >
            &times;
          </button>
        </div>
        <div class="video-wrapper">
          <div v-if="isLoading" class="loading-spinner">
            <div class="spinner"></div>
            <p>Chargement de la vidéo...</p>
          </div>
          <video
            ref="videoElement"
            :class="{ hidden: isLoading }"
            :key="videoActive.chemin"
            controls
            autoplay
            @pause="isPlaying = false"
            @play="playStarted"
            @ended="handleVideoEnd"
            @waiting="isLoading = true"
            @playing="isLoading = false"
            @timeupdate="updateProgress"
          >
            <source :src="videoActive.chemin" type="video/mp4">
            Votre navigateur ne supporte pas les vidéos HTML5.
          </video>
        </div>
        <div class="custom-controls" :class="{ 'visible': controlsVisible }">
          <button 
            @click="prevVideo" 
            class="control-btn" 
            :disabled="!canGoPrev"
            aria-label="Vidéo précédente"
          >
            ⏮
          </button>
          <button 
            @click="togglePlayPause" 
            class="control-btn play-pause-btn" 
            :aria-label="isPlaying ? 'Pause' : 'Lecture'"
          >
            {{ isPlaying ? '⏸' : '▶️' }}
          </button>
          <button 
            @click="nextVideo" 
            class="control-btn" 
            :disabled="!canGoNext"
            aria-label="Vidéo suivante"
          >
            ⏭
          </button>
          <div class="progress-container">
            <input
              type="range"
              class="progress-bar"
              :value="currentTime"
              @input="seekVideo"
              min="0"
              :max="videoDuration"
              step="0.1"
              aria-label="Barre de progression"
            >
            <span class="time-display">
              {{ formatTime(currentTime) }} / {{ formatTime(videoDuration) }}
            </span>
          </div>
          <button 
            @click="toggleFullscreen" 
            class="control-btn fullscreen-btn" 
            :aria-label="isFullscreen ? 'Quitter le plein écran' : 'Plein écran'"
            @mousedown.stop
          >
            {{ isFullscreen ? '🗗' : '🗖' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MesFormations from '@/connection/MesFormations';
import habilitationImage from '@/assets/dd.jpeg';

export default {
  name: 'HabilitationElectriqueNfc',
  data() {
    return {
      backgroundStyle: {
        backgroundImage: `url(${habilitationImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '40px 20px',
        textAlign: 'center',
      },
      chapitres: [],
      chapitreActif: null,
      moduleActif: null,
      videoActive: null,
      isPlaying: false,
      isFullscreen: false,
      isLoading: false,
      currentTime: 0,
      videoDuration: 0,
      progressInterval: null,
      fullscreenLock: false,
      controlsVisible: false,
      hideControlsTimeout: null
    };
  },
  computed: {
    currentVideos() {
      if (this.chapitreActif === null || this.moduleActif === null) return [];
      return this.chapitres[this.chapitreActif].modules[this.moduleActif].videos;
    },
    currentVideoIndex() {
      if (!this.videoActive) return -1;
      return this.currentVideos.findIndex(v => v.chemin === this.videoActive.chemin);
    },
    canGoPrev() {
      return this.currentVideoIndex > 0 || 
             (this.moduleActif !== null && this.moduleActif > 0);
    },
    canGoNext() {
      return this.currentVideoIndex < this.currentVideos.length - 1 || 
             (this.moduleActif !== null && this.moduleActif < this.chapitres[this.chapitreActif].modules.length - 1);
    }
  },
  mounted() {
    this.setupEventListeners();
    this.loadFormationData();
  },
  beforeUnmount() {
    this.cleanupEventListeners();
    clearInterval(this.progressInterval);
    clearTimeout(this.hideControlsTimeout);
  },
  methods: {
    async loadFormationData() {
      try {
        const response = await MesFormations.henfc();
        this.chapitres = this.sortFormationData(response.data);
      } catch (error) {
        console.error("Erreur de chargement des données :", error);
      }
    },
    
    extractLeadingNumber(str) {
      if (!str) return 0;
      const match = str.match(/^(\d+)/);
      return match ? parseInt(match[1]) : str.charCodeAt(0);
    },
    
    sortFormationData(data) {
      const extractLeadingNumber = (str) => {
        if (!str) return Infinity;
        const match = String(str).match(/^(\d+)(?:\s*-\s*|\s+)/);
        return match ? parseInt(match[1], 10) : Infinity;
      };

      return data
        .sort((a, b) => extractLeadingNumber(a.nom) - extractLeadingNumber(b.nom))
        .map(chapitre => ({
          ...chapitre,
          modules: chapitre.modules
            .sort((a, b) => extractLeadingNumber(a.nom) - extractLeadingNumber(b.nom))
            .map(module => ({
              ...module,
              videos: module.videos.sort((a, b) => 
                extractLeadingNumber(a.titre) - extractLeadingNumber(b.titre)
              )
            }))
        }));
    },
    
    setupEventListeners() {
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
      document.addEventListener('keydown', this.handleKeyPress);
    },
    
    cleanupEventListeners() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
      document.removeEventListener('keydown', this.handleKeyPress);
    },
    
    handleFullscreenChange() {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
    },
    
    async toggleFullscreen() {
      try {
        if (!this.isFullscreen) {
          await this.$refs.videoContainer.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch (err) {
        console.error("Erreur de bascule plein écran:", err);
      }
    },
    
    handleMouseMove(event) {
      if (event.buttons === 2) return;
      this.showControls();
      this.resetHideTimer();
    },
    
    hideControlsNow() {
      clearTimeout(this.hideControlsTimeout);
      this.controlsVisible = false;
    },
    
    showControls() {
      if (!this.controlsVisible) {
        this.controlsVisible = true;
      }
      this.resetHideTimer();
    },
    
    resetHideTimer() {
      clearTimeout(this.hideControlsTimeout);
      this.hideControlsTimeout = setTimeout(() => {
        this.controlsVisible = false;
      }, 2000);
    },
    
    async playVideo(video) {
      // Gardez l'ancienne vidéo visible pendant le chargement
      this.isLoading = true;
      
      // Préchargez la nouvelle vidéo
      const tempVideo = document.createElement('video');
      tempVideo.src = video.chemin;
      
      await new Promise((resolve) => {
        tempVideo.onloadedmetadata = resolve;
        tempVideo.onerror = resolve; // En cas d'erreur
      });

      // Maintenant mettez à jour l'état
      this.videoActive = { ...video };
      
      await this.$nextTick();
      
      try {
        await this.$refs.videoElement.play();
        this.isPlaying = true;
      } catch (e) {
        console.log("Autoplay prevented");
        this.$refs.videoElement.controls = true;
      }
      
      this.isLoading = false;
    },
    
    async playChapitre(indexChapitre) {
      this.chapitreActif = indexChapitre;
      const chapitre = this.chapitres[indexChapitre];
      
      for (const module of chapitre.modules) {
        if (module.videos && module.videos.length > 0) {
          this.moduleActif = this.chapitres[indexChapitre].modules.indexOf(module);
          await this.playVideo(module.videos[0]);
          return;
        }
      }
    },
    
    async ensureFullscreen() {
      if (!this.$refs.videoContainer || this.isFullscreen) return;
      
      try {
        await this.$refs.videoContainer.requestFullscreen();
      } catch (err) {
        console.error("Échec du plein écran:", err);
      }
    },
    
    startProgressTracking() {
      clearInterval(this.progressInterval);
      this.progressInterval = setInterval(this.updateProgress, 200);
    },
    
    updateProgress() {
      const video = this.$refs.videoElement;
      if (!video) return;
      
      this.currentTime = video.currentTime;
      this.videoDuration = video.duration || 0;
    },
    
    seekVideo(e) {
      const video = this.$refs.videoElement;
      if (!video) return;
      
      const seekTime = parseFloat(e.target.value);
      video.currentTime = seekTime;
      this.showControls();
    },
    
    formatTime(seconds) {
      if (isNaN(seconds)) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    
    async nextVideo() {
      if (this.currentVideoIndex < this.currentVideos.length - 1) {
        const wasFullscreen = this.isFullscreen;
        await this.playVideo(this.currentVideos[this.currentVideoIndex + 1]);
        if (wasFullscreen) {
          await this.ensureFullscreen();
        }
      } else {
        await this.handleModuleTransition(true);
      }
    },
    
    async prevVideo() {
      if (this.currentVideoIndex > 0) {
        const wasFullscreen = this.isFullscreen;
        await this.playVideo(this.currentVideos[this.currentVideoIndex - 1]);
        if (wasFullscreen) {
          await this.ensureFullscreen();
        }
      } else {
        await this.handleModuleTransition(false);
      }
    },
    
    async handleModuleTransition(next) {
      const modules = this.chapitres[this.chapitreActif].modules;
      let newModuleIndex = next ? this.moduleActif + 1 : this.moduleActif - 1;
      const wasFullscreen = this.isFullscreen;
      
      while (newModuleIndex >= 0 && newModuleIndex < modules.length) {
        if (modules[newModuleIndex].videos.length > 0) {
          this.moduleActif = newModuleIndex;
          const videoIndex = next ? 0 : modules[newModuleIndex].videos.length - 1;
          await this.playVideo(modules[newModuleIndex].videos[videoIndex]);
          if (wasFullscreen) {
            await this.ensureFullscreen();
          }
          return;
        }
        newModuleIndex = next ? newModuleIndex + 1 : newModuleIndex - 1;
      }
    },
    
    togglePlayPause() {
      const video = this.$refs.videoElement;
      if (!video) return;

      if (video.paused) {
        video.play().then(() => this.isPlaying = true);
      } else {
        video.pause();
        this.isPlaying = false;
      }
      this.showControls();
    },
    
    selectChapitre(i) {
      this.chapitreActif = i;
      this.moduleActif = null;
      this.videoActive = null;
    },
    
    selectModule(j) {
      this.moduleActif = j;
      this.videoActive = null;
    },
    
    async closeVideo() {
      if (this.isFullscreen) {
        await document.exitFullscreen();
      }
      this.videoActive = null;
      clearInterval(this.progressInterval);
      this.isPlaying = false;
    },
    
    handleVideoEnd() {
      this.isPlaying = false;
    },
    
    playStarted() {
      this.isPlaying = true;
      this.isLoading = false;
    },
    
    handleKeyPress(e) {
      if (!this.videoActive) return;
      
      switch (e.key) {
        case ' ':
          this.togglePlayPause();
          e.preventDefault();
          break;
        case 'ArrowRight':
          this.nextVideo();
          e.preventDefault();
          break;
        case 'ArrowLeft':
          this.prevVideo();
          e.preventDefault();
          break;
        case 'f':
          this.toggleFullscreen();
          e.preventDefault();
          break;
        case 'Escape':
          if (!this.fullscreenLock) this.closeVideo();
          e.preventDefault();
          break;
      }
    }
  }
};
</script>


<style scoped>
.habilitation-container {
  padding: 20px 30px;
  font-family: 'Segoe UI', sans-serif;
  background: #f4f7f9;
  min-height: 90vh;
}

.dashboard {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.dashboard img {
  height: 80px;
  object-fit: contain;
}

.banner {
  margin-bottom: 30px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.banner h1 {
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0;
  padding: 30px 20px;
  text-align: center;
  color: white;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  background: linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.3));
}

.grid-3col {
  display: flex;
  gap: 20px;
  justify-content: space-between;
  min-height: 70vh;
}

.column {
  flex: 1;
  background: #ffffff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow-y: auto;
  max-height: 70vh;
}

.column h2 {
  margin-bottom: 15px;
  font-size: 1.2rem;
  color: #444;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  margin-bottom: 10px;
}

button {
  width: 100%;
  padding: 10px 12px;
  background: #e9ecef;
  border: none;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  display: flex;
  align-items: center;
}

button:hover {
  background: #d0d7de;
}

button.active {
  background-color: #007bff;
  color: #fff;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chapitre-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.play-chapitre {
  background-color: #58a9f7;
  color: white;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.3s ease;
  width: fit-content;
  flex-shrink: 0;
}

.play-chapitre:hover {
  background-color: #218838;
  transform: scale(1.05);
}

.play-all-text {
  margin-left: 5px;
  font-size: 0.8em;
}

@media (max-width: 768px) {
  .play-all-text {
    display: none;
  }
}

a {
  display: block;
  padding: 10px 12px;
  background-color: #f1f8ff;
  border-radius: 6px;
  text-decoration: none;
  color: #007bff;
  transition: all 0.2s ease;
}

a:hover {
  background-color: #d6eaff;
  transform: translateX(3px);
}

.video-link {
  position: relative;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  animation: fadeIn 0.3s forwards;
}

@keyframes fadeIn {
  to { opacity: 1; }
}

.modal {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  transform: scale(0.95);
  animation: scaleIn 0.3s forwards;
}

@keyframes scaleIn {
  to { transform: scale(1); }
}

.modal.modal-large {
  width: 90vw;
  height: 90vh;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  background: #000;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  background: #000;
  color: white;
}

.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: auto;
  padding: 0;
}

.close-btn:hover {
  color: #ff5252;
  transform: scale(1.2);
}

.video-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  position: relative;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid #fff;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner p {
  color: white;
  margin: 0;
}

video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  outline: none;
  transition: opacity 0.3s ease;
  z-index: 5;
  cursor: none;
  pointer-events: none;
}

.modal:hover video {
  cursor: default;
}

.custom-controls {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  background: rgba(0, 0, 0, 0.3);
  padding: 15px 25px;
  border-radius: 10px;
  display: flex;
  gap: 15px;
  align-items: center;
  width: 90%;
  max-width: 800px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease, visibility 0.5s ease;
}

.custom-controls.visible {
  opacity: 1;
  visibility: visible;
}

.control-btn {
  font-size: 24px;
  background: transparent;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.control-btn:hover {
  color: #0056b3;
  transform: scale(1.1);
}

.control-btn:disabled {
  color: #6c757d;
  transform: none;
  cursor: not-allowed;
}

.play-pause-btn {
  color: #007bff;
  background: rgba(255, 255, 255, 0.2);
  width: 50px;
  height: 50px;
  font-size: 28px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.progress-container {
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
}

.progress-bar {
  flex-grow: 1;
  height: 5px;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  background: rgba(255,255,255,0.3);
  border-radius: 3px;
  outline: none;
  transition: height 0.2s;
}

.progress-bar:hover {
  height: 8px;
}

.progress-bar::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  transition: transform 0.2s;
}

.progress-bar::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.progress-bar::-moz-range-thumb {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
}

.time-display {
  color: white;
  font-size: 0.9rem;
  min-width: 100px;
  text-align: center;
  font-family: monospace;
}

.fullscreen-btn {
  margin-left: auto;
}

/* Styles pour le plein écran */
:fullscreen .modal.modal-large {
  width: 100% !important;
  height: 100% !important;
  max-width: none;
  border-radius: 0;
  padding: 0;
}

:fullscreen .modal-header {
  padding: 10px 20px 0;
}

:fullscreen .close-btn {
  font-size: 1.8rem;
}

:fullscreen .video-wrapper {
  width: 100vw !important;
  height: 100vh !important;
}

:fullscreen .custom-controls {
  bottom: 40px;
  padding: 20px 30px;
  max-width: 90%;
}

/* Cache les contrôles natifs */
video::-webkit-media-controls {
  display: none !important;
}

/* Responsive */
@media (max-width: 992px) {
  .grid-3col {
    flex-direction: column;
  }
  
  .column {
    width: 100%;
    margin-bottom: 20px;
    max-height: none;
  }
  
  .banner h1 {
    font-size: 2rem;
    padding: 20px 10px;
  }
}

@media (max-width: 768px) {
  .habilitation-container {
    padding: 15px;
  }
  
  .modal.modal-large {
    width: 95vw;
    height: 80vh;
  }
  
  .custom-controls {
    bottom: 20px;
    padding: 12px 15px;
    gap: 10px;
    width: 95%;
  }
  
  .control-btn {
    font-size: 20px;
    width: 35px;
    height: 35px;
  }
  
  .play-pause-btn {
    width: 45px;
    height: 45px;
    font-size: 24px;
  }
  
  .close-btn {
    font-size: 1.8rem;
    width: 35px;
    height: 35px;
  }
}

@media (max-width: 480px) {
  .banner h1 {
    font-size: 1.5rem;
  }
  
  .modal h4 {
    font-size: 1.1rem;
  }
  
  .modal-header {
    padding: 0 15px;
  }
  
  .custom-controls {
    bottom: 15px;
    padding: 10px 12px;
    gap: 8px;
    width: 95%;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .progress-container {
    order: -1;
    width: 100%;
    padding: 0 10px;
  }
  
  .control-btn {
    font-size: 18px;
    width: 30px;
    height: 30px;
  }
  
  .play-pause-btn {
    width: 40px;
    height: 40px;
    font-size: 22px;
  }
  
  .close-btn {
    font-size: 1.5rem;
    width: 30px;
    height: 30px;
  }
  
  .time-display {
    min-width: 80px;
    font-size: 0.8rem;
  }
}

.video-wrapper {
  position: relative;
}

video {
  transition: opacity 0.3s ease;
}

video.hidden {
  opacity: 0;
}
</style>